import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import MapView from "@/components/MapView";
import { Search, FileDown, Save, CloudRain, Sun, Thermometer, Snowflake, Layers, MapPin, LocateFixed } from "lucide-react";
import LocationReportView from "@/components/LocationReportView";

export default function LocationQuery() {
  const { t } = useI18n();
  const nav = useNavigate();
  const initialState = useLocation().state || {};

  const [address, setAddress] = useState(initialState.address || "");
  const [lat, setLat] = useState(initialState.lat || "");
  const [lng, setLng] = useState(initialState.lng || "");
  const [tab, setTab] = useState("address");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [siteName, setSiteName] = useState("");
  const [siteDesc, setSiteDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [quota, setQuota] = useState(null);

  // Kontingentstand laden und nach jeder Abfrage aktualisieren — sonst zeigt die
  // Seite bis zum Neuladen einen veralteten Reststand.
  const refreshQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/usage");
      setQuota(data.queries);
    } catch (_e) {
      setQuota(null);
    }
  }, []);

  useEffect(() => { refreshQuota(); }, [refreshQuota]);

  const doQuery = async (latitude, longitude) => {
    setLoading(true);
    setErr("");
    setReport(null);
    try {
      const { data } = await api.post("/location/report", { latitude, longitude });
      setReport(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
      refreshQuota();
    }
  };

  const submitAddress = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/location/geocode", { address });
      setLat(data.latitude);
      setLng(data.longitude);
      await doQuery(data.latitude, data.longitude);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  const submitCoords = async (e) => {
    e.preventDefault();
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      setErr("Ungültige Koordinaten");
      return;
    }
    await doQuery(latNum, lngNum);
  };

  const submitGeo = () => {
    if (!navigator.geolocation) {
      setErr(t("query.geo_unavailable"));
      return;
    }
    setGeoLoading(true);
    setErr("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latNum = pos.coords.latitude;
        const lngNum = pos.coords.longitude;
        setLat(latNum);
        setLng(lngNum);
        setGeoLoading(false);
        doQuery(latNum, lngNum);
      },
      () => {
        setErr(t("query.geo_denied"));
        setGeoLoading(false);
      },
      { timeout: 10000 }
    );
  };

  const downloadPdf = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const resp = await api.post(
        "/location/pdf",
        { report, site_name: siteName || report.address?.display_name || "Standortdaten" },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "standortdaten.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF erstellt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDownloading(false);
    }
  };

  const saveAsSite = async () => {
    if (!report) return;
    setSaving(true);
    try {
      const { data } = await api.post("/sites", {
        name: siteName || report.address?.display_name?.slice(0, 60) || "Neue Baustelle",
        description: siteDesc,
        address: report.address?.display_name || "",
        latitude: report.coordinates.latitude,
        longitude: report.coordinates.longitude,
        location_report: report,
      });
      toast.success("Baustelle gespeichert");
      setSaveOpen(false);
      nav(`/app/sites/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="query-page">
      <div className="mb-8">
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#1A2F24] mb-1">
          {t("query.title")}
        </h1>
        <p className="text-[#5C6B61]">{t("query.subtitle")}</p>
        {quota && !quota.unlimited && (
          <div
            data-testid="query-quota"
            className={`inline-flex items-center gap-2 mt-3 rounded-full border px-3 py-1 text-xs ${
              quota.remaining === 0
                ? "bg-[#FDF2EE] border-[#F2D9D0] text-[#A64B38]"
                : "bg-[#F1EFE7] border-[#E5E2D9] text-[#5C6B61]"
            }`}
          >
            <MapPin size={12} />
            {quota.remaining === 0
              ? t("query.quota_exhausted")
              : t("query.quota_hint", { used: quota.used, limit: quota.limit })}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-xl p-6 mb-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#F1EFE7] mb-4">
            <TabsTrigger value="address" data-testid="tab-address">{t("query.tab_address")}</TabsTrigger>
            <TabsTrigger value="coords" data-testid="tab-coords">{t("query.tab_coords")}</TabsTrigger>
            <TabsTrigger value="geo" data-testid="tab-geo">{t("query.tab_geo")}</TabsTrigger>
          </TabsList>
          <TabsContent value="address">
            <form onSubmit={submitAddress} className="flex flex-col md:flex-row gap-3">
              <Input
                data-testid="address-input"
                placeholder={t("query.address_placeholder")}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="flex-1 bg-[#F9F8F6] border-[#E5E2D9] h-11"
              />
              <Button data-testid="search-address-button" type="submit" disabled={loading} className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white h-11 px-6">
                <Search size={16} className="mr-2" /> {t("query.search")}
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="coords">
            <form onSubmit={submitCoords} className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <Label className="text-xs">{t("query.lat")}</Label>
                <Input data-testid="lat-input" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="48.137" className="bg-[#F9F8F6] border-[#E5E2D9]" />
              </div>
              <div className="flex-1">
                <Label className="text-xs">{t("query.lng")}</Label>
                <Input data-testid="lng-input" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="11.575" className="bg-[#F9F8F6] border-[#E5E2D9]" />
              </div>
              <Button data-testid="search-coords-button" type="submit" disabled={loading} className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white h-11 px-6 md:self-end">
                <Search size={16} className="mr-2" /> {t("query.search")}
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="geo">
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-[#5C6B61]">{t("query.geo_hint")}</p>
              <Button
                data-testid="geo-button"
                onClick={submitGeo}
                disabled={geoLoading || loading}
                className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white h-11 px-6"
              >
                <LocateFixed size={16} className="mr-2" />
                {geoLoading ? t("query.geo_locating") : t("query.geo_button")}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {err && (
        <div data-testid="query-error" className="bg-[#FDF2EE] border border-[#F2D9D0] text-[#A64B38] text-sm rounded-md p-3 mb-6">{err}</div>
      )}
      {loading && (
        <div className="text-[#5C6B61] text-sm py-6 text-center" data-testid="query-loading">{t("query.loading")}</div>
      )}

      {report && (
        <div className="space-y-6" data-testid="report-section">
          <div className="flex flex-wrap gap-3">
            <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
              <DialogTrigger asChild>
                <Button data-testid="save-site-button" className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white">
                  <Save size={16} className="mr-2" /> {t("query.save_site")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("query.save_site")}</DialogTitle>
                  <DialogDescription>{report.address?.display_name}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>{t("sites.name")}</Label>
                    <Input
                      data-testid="save-site-name"
                      value={siteName}
                      onChange={(e) => setSiteName(e.target.value)}
                      placeholder={report.address?.display_name?.slice(0, 40)}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>{t("sites.description")}</Label>
                    <Textarea
                      data-testid="save-site-desc"
                      value={siteDesc}
                      onChange={(e) => setSiteDesc(e.target.value)}
                      rows={3}
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="save-site-confirm" onClick={saveAsSite} disabled={saving} className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white">
                    {saving ? t("common.loading") : t("common.save")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button data-testid="download-pdf-button" onClick={downloadPdf} disabled={downloading} variant="outline" className="border-[#1A2F24] text-[#1A2F24] hover:bg-[#F1EFE7]">
              <FileDown size={16} className="mr-2" />
              {downloading ? t("common.loading") : t("query.download_pdf")}
            </Button>
          </div>

          <LocationReportView report={report} />
        </div>
      )}
    </div>
  );
}
